import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  CheckCircle, XCircle, Clock, Loader2, Image, Bot
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDeposits = ({ onRefresh }) => {
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDeposits();
  }, []);

  const loadDeposits = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/deposits`, { withCredentials: true });
      setDeposits(res.data?.deposits || []);
    } catch (err) {
      console.error('Failed to load deposits', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (deposit) => {
    if (!window.confirm(`Approve this deposit? This will add ${deposit.credits_amount?.toLocaleString()} credits to ${deposit.user_name}'s account.`)) {
      return;
    }
    try {
      await axios.post(`${API}/admin/deposits/verify`, {
        deposit_id: deposit.deposit_id,
        action: 'approve',
        admin_note: 'Approved by admin'
      }, { withCredentials: true });
      toast.success('Deposit approved!');
      loadDeposits();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error('Failed to approve deposit');
    }
  };

  const handleReject = async (deposit) => {
    const note = window.prompt('Rejection reason:', 'Payment could not be verified');
    if (note === null) return;
    
    try {
      await axios.post(`${API}/admin/deposits/verify`, {
        deposit_id: deposit.deposit_id,
        action: 'reject',
        admin_note: note || 'Rejected by admin'
      }, { withCredentials: true });
      toast.success('Deposit rejected');
      loadDeposits();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error('Failed to reject deposit');
    }
  };

  const handleAIVerify = async (deposit) => {
    try {
      const res = await axios.post(`${API}/admin/deposits/ai-verify/${deposit.deposit_id}`, {}, { withCredentials: true });
      if (res.data.auto_approved) {
        toast.success('AI verified and approved!');
        loadDeposits();
        if (onRefresh) onRefresh();
      } else {
        toast.info(res.data.message || 'AI could not verify. Manual review required.');
      }
    } catch (err) {
      toast.error('AI verification failed');
    }
  };

  const StatusBadge = ({ status }) => {
    const config = {
      pending: { color: '#808080', text: 'Pending' },
      under_review: { color: '#FFB800', text: 'Under Review' },
      approved: { color: '#00FF94', text: 'Approved' },
      rejected: { color: '#FF2E2E', text: 'Rejected' }
    };
    const c = config[status] || config.pending;
    return (
      <span 
        className="px-2 py-0.5 rounded-full text-xs font-medium"
        style={{ backgroundColor: `${c.color}20`, color: c.color }}
      >
        {c.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      {deposits.length === 0 ? (
        <div className="glass-card rounded-xl p-12 text-center">
          <CheckCircle className="w-12 h-12 mx-auto mb-4 text-[#00FF94]/50" />
          <p className="text-white/50">No deposit requests</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="glass-card rounded-xl p-4 text-center">
              <p className="text-[#FFB800] font-mono text-2xl font-bold">
                {deposits.filter(d => d.status === 'under_review').length}
              </p>
              <p className="text-white/50 text-sm">Under Review</p>
            </div>
            <div className="glass-card rounded-xl p-4 text-center">
              <p className="text-[#00FF94] font-mono text-2xl font-bold">
                {deposits.filter(d => d.status === 'approved').length}
              </p>
              <p className="text-white/50 text-sm">Approved</p>
            </div>
            <div className="glass-card rounded-xl p-4 text-center">
              <p className="text-[#FF2E2E] font-mono text-2xl font-bold">
                {deposits.filter(d => d.status === 'rejected').length}
              </p>
              <p className="text-white/50 text-sm">Rejected</p>
            </div>
          </div>

          {/* Deposit List */}
          {deposits.map(deposit => (
            <div key={deposit.deposit_id} className="glass-card rounded-xl p-6" data-testid={`deposit-${deposit.deposit_id}`}>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <p className="font-semibold">{deposit.user_name}</p>
                    <StatusBadge status={deposit.status} />
                  </div>
                  <p className="text-white/50 text-sm">{deposit.user_email}</p>
                  <p className="text-[#00F0FF] font-mono text-sm mt-1">Code: {deposit.reference_code}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-xl font-bold text-[#CCFF00]">${deposit.amount_usd?.toFixed(2)}</p>
                  <p className="text-white/50 text-sm">{deposit.credits_amount?.toLocaleString()} credits</p>
                </div>
              </div>

              {/* Details */}
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                {deposit.screenshot_url && (
                  <div>
                    <p className="text-white/50 text-xs mb-2">Screenshot</p>
                    <a href={deposit.screenshot_url} target="_blank" rel="noopener noreferrer" className="block">
                      <img 
                        src={deposit.screenshot_url} 
                        alt="Payment proof" 
                        className="w-full max-w-xs rounded-lg border border-white/10 hover:border-[#00F0FF]/50 transition-colors"
                      />
                    </a>
                  </div>
                )}
                {deposit.transaction_id && (
                  <div>
                    <p className="text-white/50 text-xs mb-2">Transaction ID</p>
                    <p className="font-mono text-sm bg-white/5 p-2 rounded">{deposit.transaction_id}</p>
                  </div>
                )}
              </div>

              <div className="text-xs text-white/40 mb-4">
                Submitted: {new Date(deposit.created_at).toLocaleString()}
              </div>

              {/* Actions */}
              {(deposit.status === 'pending' || deposit.status === 'under_review') && (
                <div className="flex gap-2 pt-4 border-t border-white/10">
                  <Button
                    onClick={() => handleApprove(deposit)}
                    className="btn-primary"
                    size="sm"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" /> Approve
                  </Button>
                  <Button
                    onClick={() => handleReject(deposit)}
                    variant="outline"
                    className="border-[#FF2E2E]/50 text-[#FF2E2E] hover:bg-[#FF2E2E]/10"
                    size="sm"
                  >
                    <XCircle className="w-4 h-4 mr-1" /> Reject
                  </Button>
                  {deposit.screenshot_url && (
                    <Button
                      onClick={() => handleAIVerify(deposit)}
                      variant="outline"
                      className="btn-secondary"
                      size="sm"
                    >
                      <Bot className="w-4 h-4 mr-1" /> AI Verify
                    </Button>
                  )}
                </div>
              )}

              {deposit.admin_note && (
                <div className="mt-4 p-3 rounded-lg bg-white/5">
                  <p className="text-xs text-white/50">Admin Note:</p>
                  <p className="text-sm">{deposit.admin_note}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default AdminDeposits;
